import { useState } from 'react';
import { ShoppingBag, Calendar, FileText, CreditCard, Repeat, User } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { WizardOption } from '../WizardOption';
import { trpc } from '@/trpc/client';

interface SaleDetailsStepProps {
  initialData: {
    paymentType: 'leasing' | 'direct' | null;
    productDescription: string;
    deliveryDate: string;
    deliveryDateKnown: boolean;
    notes: string;
    salespersonUserId: string;
  };
  /** Active location — used to scope the salesperson dropdown to staff at
   *  this branch so a Berlin admin doesn't accidentally credit a sale to
   *  a Munich employee. */
  locationId: string;
  onSubmit: (data: {
    paymentType: 'leasing' | 'direct';
    productDescription: string;
    deliveryDate: string;
    deliveryDateKnown: boolean;
    notes: string;
    salespersonUserId: string;
  }) => void;
  onBack: () => void;
}

export function SaleDetailsStep({ initialData, locationId, onSubmit, onBack }: SaleDetailsStepProps) {
  const [paymentType, setPaymentType] = useState<'leasing' | 'direct' | null>(initialData.paymentType);
  const [productDescription, setProductDescription] = useState(initialData.productDescription);
  const [deliveryDate, setDeliveryDate] = useState(initialData.deliveryDate);
  const [deliveryDateKnown, setDeliveryDateKnown] = useState(initialData.deliveryDateKnown);
  const [notes, setNotes] = useState(initialData.notes);
  const [salespersonUserId, setSalespersonUserId] = useState(initialData.salespersonUserId);

  // Salespeople assignable at this branch. Served by a dedicated endpoint
  // rather than `user.list`: that one needs the `users:view` permission (so it
  // returned nothing at all for staff/senior_staff/trainee) and it hid
  // multi-branch sellers, because it filtered on the primary location only.
  const { data: eligibleUsers } = trpc.user.listSalespeople.useQuery({ locationId });

  const canSubmit = paymentType !== null && salespersonUserId.length > 0;

  const handleSubmit = () => {
    if (!paymentType) return;
    onSubmit({
      paymentType,
      productDescription: productDescription.trim(),
      deliveryDate,
      deliveryDateKnown,
      notes: notes.trim(),
      salespersonUserId,
    });
  };

  return (
    <div>
      {/* Payment type */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-slate-300 mb-3">Zahlungsart *</label>
        <div className="grid grid-cols-2 gap-3">
          <WizardOption
            icon={CreditCard}
            title="Kauf"
            description="Direktkauf"
            selected={paymentType === 'direct'}
            onClick={() => setPaymentType('direct')}
          />
          <WizardOption
            icon={Repeat}
            title="Leasing"
            description="36 Monate"
            selected={paymentType === 'leasing'}
            onClick={() => setPaymentType('leasing')}
          />
        </div>
      </div>

      {/* Salesperson — attributes the sale on the Filialleiter dashboard.
          Without an explicit pick, every walk-in entry would be credited to
          whoever clicked through the wizard (often a super admin). */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-slate-300 mb-2">Verkäufer *</label>
        <div className="relative">
          <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <select
            value={salespersonUserId}
            onChange={(e) => setSalespersonUserId(e.target.value)}
            className="w-full pl-10 pr-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-orange-500"
          >
            {!salespersonUserId && <option value="">Bitte wählen…</option>}
            {(eligibleUsers ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Product description */}
      <div className="space-y-4">
        <Input
          label="Fahrrad / Produkt"
          placeholder="z.B. Orbea Gain D30, Größe M"
          value={productDescription}
          onChange={(e) => setProductDescription(e.target.value)}
          leftIcon={<ShoppingBag className="w-4 h-4" />}
        />

        {/* Delivery date - shown once a payment type is selected */}
        {paymentType !== null && (
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Liefertermin</label>
            <div className="flex gap-3 mb-3">
              <button
                onClick={() => setDeliveryDateKnown(true)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  deliveryDateKnown
                    ? 'bg-orange-500/20 border border-orange-500/50 text-orange-300'
                    : 'bg-slate-700/50 border border-slate-600 text-slate-400 hover:bg-slate-700'
                }`}
              >
                Datum bekannt
              </button>
              <button
                onClick={() => { setDeliveryDateKnown(false); setDeliveryDate(''); }}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !deliveryDateKnown
                    ? 'bg-orange-500/20 border border-orange-500/50 text-orange-300'
                    : 'bg-slate-700/50 border border-slate-600 text-slate-400 hover:bg-slate-700'
                }`}
              >
                Noch unbekannt
              </button>
            </div>
            {deliveryDateKnown && (
              <Input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                leftIcon={<Calendar className="w-4 h-4" />}
              />
            )}
          </div>
        )}

        {/* Notes */}
        <Input
          label="Notizen"
          placeholder="Zusätzliche Informationen..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          leftIcon={<FileText className="w-4 h-4" />}
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-3 mt-6">
        <Button variant="ghost" onClick={onBack} className="flex-1">
          Zurück
        </Button>
        <Button onClick={handleSubmit} className="flex-1" disabled={!canSubmit}>
          Weiter
        </Button>
      </div>
    </div>
  );
}
